import { z } from 'zod';
import { nutrientsSchema } from '../nutrients.js';
export const sexSchema = z.enum(['female', 'male', 'unspecified']);
export const activityLevelSchema = z.enum(['sedentary', 'light', 'moderate', 'very', 'extra']);
export const goalSchema = z.enum(['lose', 'maintain', 'gain', 'performance']);
/**
 * The most anybody is given a deficit or surplus for, per week.
 *
 * Here rather than in the domain because both need it and `domain` depends
 * on this package, not the other way round. It used to exist only inside
 * `goalAdjustment`, capping at 1, while this file accepted 2 — so the form
 * offered a rate the app quietly halved.
 */
export const MAX_GOAL_RATE_KG_PER_WEEK = 1;
export const constraintKindSchema = z.enum(['allergen', 'exclusion', 'dislike', 'preference']);
export const memberConstraintSchema = z.object({
    id: z.string().uuid(),
    kind: constraintKindSchema,
    value: z.string(),
    /** A hard constraint is never violated by a plan (M-43). */
    hard: z.boolean(),
});
export const memberTargetSchema = z.object({
    energyKcal: z.number(),
    proteinG: z.number(),
    fatG: z.number(),
    carbG: z.number(),
    fibreG: z.number().nullable(),
    sugarG: z.number().nullable(),
    trainingDayEnergyKcal: z.number().nullable(),
    trainingDayCarbG: z.number().nullable(),
    source: z.enum(['computed', 'manual']),
    effectiveFrom: z.string(),
});
export const memberSchema = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    displayName: z.string(),
    sex: sexSchema,
    dob: z.string().nullable(),
    heightCm: z.number().nullable(),
    activityLevel: activityLevelSchema,
    goal: goalSchema,
    goalRate: z.number().nullable(),
    /** What they are aiming for, so the deficit can stop. Null for most people. */
    targetWeightKg: z.number().nullable(),
    /** A daily step target, or null for none. There is no default (rule 2). */
    stepTarget: z.number().int().nullable(),
    /** Minutes of sleep a night they are aiming for, or null for none. No default either. */
    sleepTargetMin: z.number().int().nullable(),
    /** Their own daily target for drinks in ml, or null to use the default (ADR-039). */
    fluidTargetMl: z.number().int().nullable(),
    /** The target for drinks that applies: theirs, or the default for their age and sex. */
    fluidTargetEffectiveMl: z.number().int(),
    /** The sleep source family they trust, or null for automatic (ADR-034). */
    sleepSource: z.string().nullable(),
    /** The share of a day's exercise added to its allowance, 0 to 1 (ADR-035). */
    eatBackFraction: z.number(),
    /** Derived from `dob`, never stored (ADR-015). */
    isChild: z.boolean(),
    latestWeightKg: z.number().nullable(),
    target: memberTargetSchema.nullable(),
    constraints: z.array(memberConstraintSchema),
    /** When the person left the household; null while they are in it. */
    archivedAt: z.string().nullable(),
    /** True when a picture is stored; fetched from GET /members/:id/avatar. */
    hasAvatar: z.boolean(),
});
export const memberInputSchema = z.object({
    displayName: z.string().min(1).max(120),
    sex: sexSchema.default('unspecified'),
    dob: z.string().date().nullish(),
    heightCm: z.number().min(50).max(260).nullish(),
    activityLevel: activityLevelSchema.default('moderate'),
    goal: goalSchema.default('maintain'),
    /**
     * Refused above the figure the equation honours.
     *
     * This accepted 2 while `goalAdjustment` capped at 1, so the form offered
     * a rate the app quietly halved. One limit, taken from the domain, so what
     * is asked for is what happens.
     */
    goalRate: z.number().min(0).max(MAX_GOAL_RATE_KG_PER_WEEK).nullish(),
    /**
     * Bounded where the column is. Not judged against anything: ADR-026 lets
     * the app show somebody their place on a published scale and still refuses
     * to tell them what to weigh.
     */
    targetWeightKg: z.number().min(25).max(400).nullish(),
    /** Below 500 a day is not a target, it is a typo; above 100,000 likewise. */
    stepTarget: z.number().int().min(500).max(100000).nullish(),
    /**
     * Between one hour and sixteen. Left out, it is left as it was, so a form
     * that does not show it cannot clear it; null clears it.
     */
    sleepTargetMin: z.number().int().min(60).max(960).nullish(),
    /** Null goes back to the default for their age and sex (ADR-039). */
    fluidTargetMl: z.number().int().min(250).max(10000).nullish(),
    /** A sleep source family to prefer; null is automatic. Left out, it is left as it was. */
    sleepSource: z.string().trim().min(1).max(100).nullish(),
    /** Left out, it is left as it was. */
    eatBackFraction: z.number().min(0).max(1).optional(),
});
/** A family the person's sleep comes from, for choosing between them (ADR-034). */
export const sleepSourceSchema = z.object({
    family: z.string(),
    nights: z.number().int(),
    lastDate: z.string(),
    /** Includes nights from a direct connection, not only the phone. */
    direct: z.boolean(),
});
export const manualTargetSchema = z.object({
    energyKcal: z.number().min(500).max(10000),
    proteinG: z.number().min(0).max(500),
    fatG: z.number().min(0).max(500),
    carbG: z.number().min(0).max(1500),
    fibreG: z.number().min(0).max(200).nullish(),
    sugarG: z.number().min(0).max(500).nullish(),
    trainingDayEnergyKcal: z.number().min(500).max(12000).nullish(),
    trainingDayCarbG: z.number().min(0).max(1500).nullish(),
});
export const constraintInputSchema = z.object({
    kind: constraintKindSchema,
    value: z.string().min(1).max(80),
    hard: z.boolean().default(true),
});
export const metricInputSchema = z
    .object({
    date: z.string().date(),
    weightKg: z.number().min(20).max(400).nullish(),
    bodyFatPct: z.number().min(2).max(70).nullish(),
    steps: z.number().int().min(0).max(200000).nullish(),
    restingHr: z.number().int().min(20).max(150).nullish(),
    sleepMin: z.number().int().min(0).max(1440).nullish(),
})
    .refine((v) => v.weightKg != null ||
    v.bodyFatPct != null ||
    v.steps != null ||
    v.restingHr != null ||
    v.sleepMin != null, { message: 'Record at least one measurement' });
export const metricSchema = z.object({
    date: z.string(),
    weightKg: z.number().nullable(),
    bodyFatPct: z.number().nullable(),
    steps: z.number().int().nullable(),
    restingHr: z.number().int().nullable(),
    sleepMin: z.number().int().nullable(),
    source: z.string(),
    // Everything else a device may say about a day (0.12.0). Body figures
    // are decrypted here and nowhere else.
    fatMassKg: z.number().nullable().default(null),
    muscleMassKg: z.number().nullable().default(null),
    hydrationKg: z.number().nullable().default(null),
    boneMassKg: z.number().nullable().default(null),
    visceralFat: z.number().nullable().default(null),
    systolicMmhg: z.number().nullable().default(null),
    diastolicMmhg: z.number().nullable().default(null),
    spo2Pct: z.number().nullable().default(null),
    hrMinBpm: z.number().int().nullable().default(null),
    hrAvgBpm: z.number().int().nullable().default(null),
    hrMaxBpm: z.number().int().nullable().default(null),
    temperatureC: z.number().nullable().default(null),
    pulseWaveVelocity: z.number().nullable().default(null),
    activeEnergyKj: z.number().nullable().default(null),
    distanceM: z.number().nullable().default(null),
    sleepDeepMin: z.number().int().nullable().default(null),
    sleepLightMin: z.number().int().nullable().default(null),
    sleepRemMin: z.number().int().nullable().default(null),
    sleepHrAvg: z.number().int().nullable().default(null),
    /** Anything the provider sent that has no column, keyed by its own type code. */
    extra: z.record(z.number()).nullable().default(null),
});
/**
 * One day on the Statistics page (M-81, M-82): what was eaten, what was
 * burnt in sessions, and what the devices said. Every day of the window is
 * present, with nulls where nothing was recorded, so charts share an axis.
 * Consumed is null on a day with no meal logged at all (a gap, not a fast).
 */
export const memberStatsDaySchema = z.object({
    date: z.string(),
    consumedKcal: z.number().nullable(),
    /** Energy of the day's recorded sessions, in kcal. */
    exerciseKcal: z.number().nullable(),
    steps: z.number().int().nullable(),
    distanceM: z.number().nullable(),
    /** The main sleep, from the source the person's preference picks (ADR-034). */
    sleepMin: z.number().int().nullable(),
    sleepDeepMin: z.number().int().nullable(),
    sleepRemMin: z.number().int().nullable(),
    /** Naps that day, kept apart from the night. */
    napMin: z.number().int().nullable(),
    /** Millilitres drunk, from drinks logged (ADR-039); null when nothing was. */
    fluidMl: z.number().nullable(),
    restingHr: z.number().int().nullable(),
    weightKg: z.number().nullable(),
    bodyFatPct: z.number().nullable(),
    muscleMassKg: z.number().nullable(),
    hydrationKg: z.number().nullable(),
    systolicMmhg: z.number().nullable(),
    diastolicMmhg: z.number().nullable(),
    spo2Pct: z.number().int().nullable(),
    /** The day as measured (ADR-025); `restingHr` may have been derived from it. */
    hrMinBpm: z.number().int().nullable(),
    hrAvgBpm: z.number().int().nullable(),
    hrMaxBpm: z.number().int().nullable(),
    /** Whether `restingHr` is ours rather than the device's (ADR-025). */
    restingHrDerived: z.boolean().default(false),
});
export const memberStatsSchema = z.object({
    from: z.string(),
    to: z.string(),
    days: z.array(memberStatsDaySchema),
});
export const weightTrendSchema = z.object({
    points: z.array(z.object({ date: z.string(), weightKg: z.number() })),
    trend: z.array(z.object({ date: z.string(), weightKg: z.number() })),
    /** Kilograms per week implied by the trend; null until there is a fortnight. */
    rateKgPerWeek: z.number().nullable(),
});
export const dailyTotalsSchema = z.object({
    date: z.string(),
    memberId: z.string().uuid(),
    memberName: z.string(),
    consumed: nutrientsSchema,
    target: memberTargetSchema.nullable(),
    /** Energy burned beyond resting, from logged or synced activity. */
    expenditureKcal: z.number().nullable(),
    /** What a device said about the day: the night before it, the steps in it, a weigh-in, a cuff. */
    readings: z.object({
        /** The main sleep, from the preferred source (ADR-034). */
        sleepMin: z.number().int().nullable(),
        sleepDeepMin: z.number().int().nullable(),
        /** Naps that day, apart from the night; null when there were none. */
        napMin: z.number().int().nullable(),
        steps: z.number().int().nullable(),
        restingHr: z.number().int().nullable(),
        weightKg: z.number().nullable(),
        systolicMmhg: z.number().nullable(),
        diastolicMmhg: z.number().nullable(),
    }),
    isTrainingDay: z.boolean(),
    meals: z.array(z.object({
        id: z.string().uuid(),
        slot: z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'custom']),
        title: z.string(),
        quantity: z.number(),
        unit: z.string(),
        energyKcal: z.number().nullable(),
        source: z.enum(['planned', 'adjusted', 'substituted', 'ad_hoc']),
        /** Their own figure, which nothing can recompute (ADR-017). */
        estimated: z.boolean(),
        planSlotId: z.string().uuid().nullable(),
        /** What was logged: a food, a recipe, or neither for a free-form figure. */
        foodId: z.string().uuid().nullable(),
        recipeId: z.string().uuid().nullable(),
        /** The macros as logged, so an entry can be corrected in place. */
        proteinG: z.number().nullable(),
        carbG: z.number().nullable(),
        fatG: z.number().nullable(),
        /** The rest of the label, from the same snapshot; null where the food does not say. */
        sugarG: z.number().nullable(),
        fibreG: z.number().nullable(),
        saltG: z.number().nullable(),
        /** Millilitres, for a drink whose amount is known (ADR-039). */
        fluidMl: z.number().nullable().default(null),
    })),
    /**
     * What was drunk against the day's target (ADR-039). `unknownCount` is the
     * drinks whose amount could not be worked out, which count for nothing.
     */
    fluid: z
        .object({
        ml: z.number(),
        targetMl: z.number().int(),
        unknownCount: z.number().int(),
    })
        .nullable()
        .default(null),
    /**
     * The day's allowance (ADR-035): the target, plus a share of the day's
     * exercise, recorded and planned. Null without a target.
     */
    allowance: z
        .object({
        baseKcal: z.number(),
        recordedKcal: z.number(),
        plannedKcal: z.number(),
        eatBackFraction: z.number(),
        addedKcal: z.number(),
        energyKcal: z.number(),
        carbG: z.number(),
    })
        .nullable()
        .default(null),
    /** How much of `consumed` was asserted rather than computed. */
    estimatedKcal: z.number(),
    /** Planned for this person today, with whatever they have said about it. */
    planned: z.array(z.object({
        planSlotId: z.string().uuid(),
        slot: z.enum(['breakfast', 'lunch', 'dinner', 'snack', 'drink', 'custom']),
        title: z.string(),
        /** Typed rather than a recipe or food: nothing to log from (ADR-042). */
        freeText: z.boolean().default(false),
        servings: z.number().nullable(),
        grams: z.number().nullable(),
        state: z.enum(['offered', 'accepted', 'declined']),
        slotStatus: z.enum(['planned', 'cooked', 'skipped']),
        logged: z.boolean(),
    })),
});
//# sourceMappingURL=member.js.map